import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@nextiva/next-ui-components';
import { SwitchWrapper } from 'components/Switch';
import { Hint } from 'components/PhoneNumberHint';
import { SvgOpenClose } from 'components/SvgOpenClose';
import { actionTypes, CallForwardContext } from 'containers/CallForwarding';
import { formatToTestId } from '../../utils/validators';
import { RevealCardStyles } from './RevealCard.styles.ts';

const RevealSvgIcon = styled(SvgOpenClose)`
  transform: ${({ sectionName }) =>
    sectionName ? 'rotate(-180deg)' : 'rotate(0deg)'};
`;

const RevealSvgTop = styled(RevealSvgIcon)`
  transition: all 0.2s;
  margin-left: 15px;
`;

const Description = styled('div')`
  margin-bottom: ${({ sectionName }) => (sectionName ? '29px' : '0')};
`;

const Body = styled('div')`
  position: relative;
  height: ${({ sectionName }) => (sectionName ? '100%' : '0')};
  overflow: hidden;
`;

export const RevealCard = ({
  children,
  description = '',
  handleSwitchClick = () => {},
  sectionName = '',
  title = '',
  tooltip,
}) => {
  const { state, dispatch } = useContext(CallForwardContext);
  const { control } = useFormContext();

  const toggleVisibility = () => {
    dispatch({
      type: actionTypes.SECTION_VISIBILITY_TOGGLED,
      payload: sectionName,
    });
  };

  const titleTestId = formatToTestId(title);
  const descriptionTestId = formatToTestId(description);

  return (
    <RevealCardStyles>
      <div className="header">
        <div
          className="toggle-button"
          data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwarding-RevealCard-Header"
        >
          <button
            className="header-group toggle-button"
            onClick={toggleVisibility}
            type="button"
            data-testid={`PlatformAdminUI-TeamVoiceSettings-CallForwarding-RevealCard-ToggleButton-${titleTestId}`}
          >
            {title}
            <Hint tooltipText={tooltip} />
          </button>
          <span className="header-group">
            <Controller
              control={control}
              defaultValue={false}
              name={`${sectionName}.enabled`}
              render={({ field }) => {
                return (
                  <SwitchWrapper
                    {...field}
                    label={`${sectionName} enabled`}
                    checked={field.value || false}
                    onChange={(event) => {
                      handleSwitchClick({ event, field });
                    }}
                    data-testid={`PlatformAdminUI-TeamVoiceSettings-CallForwarding-RevealCard-ToggleSwitcher-${titleTestId}`}
                  />
                );
              }}
            />
            <button
              aria-label={`toggle ${sectionName} section visibility`}
              className="svg-wrapper"
              onClick={toggleVisibility}
              type="button"
              data-testid={`PlatformAdminUI-TeamVoiceSettings-CallForwarding-RevealCard-Accordion-${sectionName}`}
            >
              <RevealSvgTop sectionName={state.openSections[sectionName]} />
            </button>
          </span>
        </div>
      </div>
      <Description
        className="helper-text"
        sectionName={state.openSections[sectionName]}
        data-testid={`PlatformAdminUI-TeamVoiceSettings-CallForwarding-RevealCard-HelperText-${descriptionTestId}`}
      >
        {description}
      </Description>

      <Body sectionName={state.openSections[sectionName]}>
        {children}
        <button
          className="close-button"
          onClick={toggleVisibility}
          type="button"
          data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwarding-RevealCard-CloseButton"
        >
          <div>Close this section</div>
          <RevealSvgIcon sectionName={state.openSections[sectionName]} />
        </button>
      </Body>
      <div className="footer" />
    </RevealCardStyles>
  );
};

RevealCard.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  handleSwitchClick: PropTypes.func.isRequired,
  sectionName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
  description: PropTypes.string,
};
